import type { YearlySignupsData } from "@investhinks/api-client";
import React, { useMemo } from "react";
import Skeleton from "react-loading-skeleton";

import SafeECharts from "../../stats/SafeECharts";

interface YearlySignUpsGraphProps {
  currentYear: number;
  previousYear: number;
  twoYearsAgo: number;
  currentYearData: YearlySignupsData | null;
  previousYearData: YearlySignupsData | null;
  twoYearsAgoData: YearlySignupsData | null;
  isLoading: boolean;
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function buildMonthArray(data: YearlySignupsData | null): number[] {
  if (!data) return Array(12).fill(0);
  return Array.from({ length: 12 }, (_, i) => data.perMonth[String(i + 1)]?.total ?? 0);
}

const YearlySignUpsGraph: React.FC<YearlySignUpsGraphProps> = ({
  currentYear,
  previousYear,
  twoYearsAgo,
  currentYearData,
  previousYearData,
  twoYearsAgoData,
  isLoading,
}) => {
  const primaryColor = getComputedStyle(document.documentElement).getPropertyValue("--primary").trim();
  const defaultColor = getComputedStyle(document.documentElement).getPropertyValue("--default").trim();
  const successColor = getComputedStyle(document.documentElement).getPropertyValue("--success").trim();

  const currentYearArray = useMemo(() => buildMonthArray(currentYearData), [currentYearData]);
  const previousYearArray = useMemo(() => buildMonthArray(previousYearData), [previousYearData]);
  const twoYearsAgoArray = useMemo(() => buildMonthArray(twoYearsAgoData), [twoYearsAgoData]);

  if (isLoading) return <Skeleton height={300} />;

  return (
    <SafeECharts
      style={{ height: "300px" }}
      option={{
        tooltip: { trigger: "axis" },
        legend: { data: [String(currentYear), String(previousYear), String(twoYearsAgo)] },
        grid: { containLabel: true, top: 30, right: 10, left: 10, bottom: 10 },
        xAxis: { type: "category", data: MONTH_LABELS },
        yAxis: { type: "value", minInterval: 1 },
        series: [
          { name: String(currentYear), type: "line", data: currentYearArray, color: primaryColor, smooth: true },
          { name: String(previousYear), type: "line", data: previousYearArray, color: defaultColor, smooth: true },
          { name: String(twoYearsAgo), type: "line", data: twoYearsAgoArray, color: successColor, smooth: true },
        ],
      }}
    />
  );
};

export default YearlySignUpsGraph;
